// ============================================
// Memory Edit Module - Edit Sheet (ES6)
// 하단 시트 편집 모달
// XSS 방지: 모든 사용자 입력은 escapeHtml로 처리됨
// ============================================

import { escapeHtml } from '../utils.js';
import { ModalManager } from '../modal-manager.js';
import { LAYER_CONFIG } from './accordion.js';
import { getEditingItem, getEditingLayer, setEditingState, clearEditingState } from './state.js';

// 레이어별 폼 필드 가져오기
import { getEventEditFields, getEventCreateFields, validateEventData, transformEventFormData } from './layers/event.js';
import { getQuoteEditFields, getQuoteCreateFields, validateQuoteData, transformQuoteFormData } from './layers/quote.js';
import { getSummaryEditFields, validateSummaryData, transformSummaryFormData } from './layers/summary.js';

// ============================================
// 편집 시트 열기
// ============================================

export function openEditSheet(layer, item = null, onSave) {
  closeEditSheet();

  const isCreate = !item;
  setEditingState(layer, item);

  const config = LAYER_CONFIG[layer];
  const title = isCreate ? `${config.title} 추가` : `${config.title} 수정`;
  const fields = isCreate ? getCreateFields(layer) : getEditFields(layer, item);

  const modal = document.createElement('div');
  modal.id = 'memoryEditSheet';
  modal.className = 'bottom-sheet-overlay';

  // 안전한 HTML 생성 (모든 동적 값은 escapeHtml 처리)
  const safeTitle = escapeHtml(title);

  modal.innerHTML = `
    <div class="bottom-sheet-backdrop" data-action="closeEditSheet"></div>
    <div class="bottom-sheet memory-edit-sheet">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-header">
        <h3 class="edit-sheet-title">${safeTitle}</h3>
        <button class="edit-sheet-close" data-action="closeEditSheet">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="bottom-sheet-body">
        <form id="memoryEditForm" class="memory-edit-form">
          ${renderFormFields(fields)}
        </form>
        <div id="editFormErrors" class="edit-form-errors"></div>
      </div>
      <div class="bottom-sheet-footer">
        <button class="edit-sheet-btn cancel" data-action="closeEditSheet">취소</button>
        <button class="edit-sheet-btn save" data-action="saveMemory" data-layer="${escapeHtml(layer)}" ${item?.id ? `data-id="${escapeHtml(item.id)}"` : ''}>
          ${isCreate ? '추가' : '저장'}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // 이벤트 설정
  setupEditSheetEvents(modal, layer, isCreate, onSave);

  // ModalManager 등록
  ModalManager.register('memoryEditSheet', closeEditSheet, 'bottomSheet');
  ModalManager.push('memoryEditSheet');

  // 애니메이션
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('open');
    });
  });
}

// ============================================
// 편집 시트 닫기
// ============================================

export function closeEditSheet() {
  const modal = document.getElementById('memoryEditSheet');
  if (modal) {
    modal.classList.remove('open');
    setTimeout(() => {
      modal.remove();
    }, 300);

    ModalManager.remove('memoryEditSheet', true);
    ModalManager.unregister('memoryEditSheet');
  }
  clearEditingState();
}

// ============================================
// 이벤트 설정
// ============================================

function setupEditSheetEvents(modal, layer, isCreate, onSave) {
  modal.addEventListener('click', async (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;

    if (action === 'closeEditSheet') {
      closeEditSheet();
    } else if (action === 'saveMemory') {
      await handleSave(modal, layer, isCreate, onSave);
    }
  });

  // 폼 제출 방지
  const form = modal.querySelector('#memoryEditForm');
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      handleSave(modal, layer, isCreate, onSave);
    });
  }
}

// ============================================
// 저장 처리
// ============================================

async function handleSave(modal, layer, isCreate, onSave) {
  const form = modal.querySelector('#memoryEditForm');
  const errorsContainer = modal.querySelector('#editFormErrors');
  const saveBtn = modal.querySelector('[data-action="saveMemory"]');

  // 폼 데이터 수집
  const formData = collectFormData(form);

  // 유효성 검사
  const errors = validateFormData(layer, formData);
  if (errors.length > 0) {
    // 에러 메시지를 안전하게 표시 (textContent 사용 대신 escapeHtml)
    errorsContainer.innerHTML = errors.map(e => `<div class="edit-error">${escapeHtml(e)}</div>`).join('');
    return;
  }

  // 데이터 변환
  const transformedData = transformFormData(layer, formData);

  // 저장 버튼 로딩 상태
  saveBtn.disabled = true;
  saveBtn.innerHTML = '<div class="spinner small"></div>';

  try {
    // 콜백 호출
    if (onSave) {
      const editingItem = getEditingItem();
      await onSave(layer, transformedData, isCreate, editingItem?.id);
    }

    closeEditSheet();
  } catch (error) {
    console.error('[EditSheet] 저장 오류:', error);
    errorsContainer.innerHTML = `<div class="edit-error">저장에 실패했습니다: ${escapeHtml(error.message)}</div>`;
    saveBtn.disabled = false;
    saveBtn.textContent = isCreate ? '추가' : '저장';
  }
}

// ============================================
// 폼 필드 렌더링 (모든 값 escapeHtml 처리)
// ============================================

function renderFormFields(fields) {
  return fields.map(field => {
    const required = field.required ? '<span class="required">*</span>' : '';
    const safeName = escapeHtml(field.name);
    const safeLabel = escapeHtml(field.label);
    const safePlaceholder = escapeHtml(field.placeholder || '');
    const safeValue = escapeHtml(field.value || '');

    switch (field.type) {
      case 'textarea':
        return `
          <div class="memory-edit-form-group">
            <label class="memory-edit-form-label">${safeLabel}${required}</label>
            <textarea
              name="${safeName}"
              class="memory-edit-form-textarea"
              placeholder="${safePlaceholder}"
              ${field.required ? 'required' : ''}
            >${safeValue}</textarea>
          </div>
        `;

      case 'select':
        const options = field.options.map(opt => {
          const safeOptValue = escapeHtml(opt.value);
          const safeOptLabel = escapeHtml(opt.label);
          return `<option value="${safeOptValue}" ${opt.value === field.value ? 'selected' : ''}>${safeOptLabel}</option>`;
        }).join('');

        return `
          <div class="memory-edit-form-group">
            <label class="memory-edit-form-label">${safeLabel}${required}</label>
            <select name="${safeName}" class="memory-edit-form-select">
              ${options}
            </select>
          </div>
        `;

      default: // text
        return `
          <div class="memory-edit-form-group">
            <label class="memory-edit-form-label">${safeLabel}${required}</label>
            <input
              type="text"
              name="${safeName}"
              class="memory-edit-form-input"
              value="${safeValue}"
              placeholder="${safePlaceholder}"
              ${field.required ? 'required' : ''}
            >
          </div>
        `;
    }
  }).join('');
}

// ============================================
// 레이어별 폼 필드 가져오기
// ============================================

function getEditFields(layer, item) {
  switch (layer) {
    case 'event':
      return getEventEditFields(item);
    case 'quote':
      return getQuoteEditFields(item);
    case 'summary':
      return getSummaryEditFields(item);
    default:
      return [];
  }
}

function getCreateFields(layer) {
  switch (layer) {
    case 'event':
      return getEventCreateFields();
    case 'quote':
      return getQuoteCreateFields();
    default:
      return [];
  }
}

// ============================================
// 폼 데이터 수집
// ============================================

function collectFormData(form) {
  const formData = {};
  const inputs = form.querySelectorAll('input, textarea, select');

  inputs.forEach(input => {
    formData[input.name] = input.value;
  });

  return formData;
}

// ============================================
// 유효성 검사
// ============================================

function validateFormData(layer, formData) {
  switch (layer) {
    case 'event':
      return validateEventData(formData);
    case 'quote':
      return validateQuoteData(formData);
    case 'summary':
      return validateSummaryData(formData);
    default:
      return [];
  }
}

// ============================================
// 데이터 변환
// ============================================

function transformFormData(layer, formData) {
  switch (layer) {
    case 'event':
      return transformEventFormData(formData);
    case 'quote':
      return transformQuoteFormData(formData);
    case 'summary':
      return transformSummaryFormData(formData);
    default:
      return formData;
  }
}
